<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>UGC Assets</span>

            <v-btn small color="primary" @click="loadAssets" :loading="loading">
              Refresh
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert
              v-if="error"
              type="error"
              dense
              outlined
              class="mb-4"
            >
              {{ error }}
            </v-alert>

            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  label="Søg fil, virksomhed eller influencer"
                  outlined
                  dense
                  clearable
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filterStatus"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Status"
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-simple-table v-if="filteredAssets.length">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Filnavn</th>
                  <th>Status</th>
                  <th>Virksomhed</th>
                  <th>Influencer</th>
                  <th>Type</th>
                  <th>Størrelse</th>
                  <th>Pris</th>
                  <th>Rettigheder</th>
                  <th>Rettighedsperiode</th>
                  <th>Buyout</th>
                  <th>Ekstra vilkår</th>
                  <th>Tilgængelig indtil</th>
                  <th>Oprettet</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="asset in filteredAssets" :key="asset.id">
                  <td>{{ asset.id }}</td>
                  <td>{{ asset.original_filename }}</td>
                  <td>
                    <v-chip x-small dark :color="getStatusColor(asset.status)">
                      {{ formatStatusLabel(asset) }}
                    </v-chip>
                  </td>
                  <td>{{ getBusinessName(asset) }}</td>
                  <td>{{ getInfluencerName(asset) }}</td>
                  <td>{{ asset.mime_type }}</td>
                  <td>{{ formatBytes(asset.file_size) }}</td>
                  <td>{{ formatPrice(asset.ugc_price) }}</td>
                  <td>{{ formatUsageRights(asset.usage_rights) }}</td>
                  <td>{{ formatRightsPeriod(asset.rights_period) }}</td>
                  <td>{{ asset.buyout ? 'Ja' : 'Nej' }}</td>
                  <td>{{ asset.extra_terms || '-' }}</td>
                  <td>{{ asset.available_until || "-" }}</td>
                  <td>{{ asset.created_at }}</td>
                  <td>
                    <div class="d-flex" style="gap: 8px;">
                        <v-btn
                        x-small
                        color="primary"
                        :disabled="Number(asset.status) === ugcAssetStatus.DELETED || !!asset.deleted_at_storage"
                        @click="openPreview(asset)"
                        >
                        Se
                        </v-btn>

                        <v-btn
                        x-small
                        color="error"
                        :loading="deletingAssetId === asset.id"
                        :disabled="Number(asset.status) === ugcAssetStatus.DELETED || !!asset.deleted_at_storage"
                        @click="removeAsset(asset)"
                        >
                        Slet
                        </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div v-else class="text--secondary">
              Ingen UGC aktiver fundet.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showPreviewDialog" max-width="900">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
            <span>
                {{ previewAsset ? previewAsset.original_filename : "UGC Preview" }}
            </span>

            <v-btn icon @click="closePreview">
                <v-icon>fal fa-times</v-icon>
            </v-btn>
            </v-card-title>

            <v-card-text>
            <div v-if="previewLoading" class="text-center py-8">
                Loading preview...
            </div>

            <video
                v-else-if="previewUrl"
                :src="previewUrl"
                controls
                style="width: 100%; max-height: 70vh;"
            ></video>

            <div v-else class="text--secondary">
                Ingen preview tilgængelig.
            </div>
            </v-card-text>
        </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";

const UGC_ASSET_STATUS = {
  PENDING_UPLOAD: 0,
  READY: 1,
  FAILED: 2,
  DELETED: 3,
};

export default {
  name: "ModeratorUgcAssets",

  data() {
  return {
    loading: false,
    error: "",
    assets: [],
    search: "",
    filterStatus: "all",
    ugcAssetStatus: UGC_ASSET_STATUS,
    statusOptions: [
        { text: "Alle", value: "all" },
        { text: "Afventer upload", value: UGC_ASSET_STATUS.PENDING_UPLOAD },
        { text: "Klar", value: UGC_ASSET_STATUS.READY },
        { text: "Fejlede", value: UGC_ASSET_STATUS.FAILED },
        { text: "Slettet", value: UGC_ASSET_STATUS.DELETED },
    ],
    showPreviewDialog: false,
    previewUrl: "",
    previewAsset: null,
    previewLoading: false,
    deletingAssetId: null,
  };
},

  computed: {
    filteredAssets() {
        let items = this.assets || [];

        items = items.filter(item => {
            if (item.deleted_at_storage) {
            return false;
            }

            if (!item.available_until) {
            return true;
            }

            return new Date(item.available_until).getTime() > Date.now();
        });

        if (this.filterStatus !== "all") {
            items = items.filter(item => Number(item.status) === Number(this.filterStatus));
        }

        if (this.search) {
            const q = this.search.toLowerCase();

            items = items.filter(item => {
            const filename = (item.original_filename || "").toLowerCase();
            const business = this.getBusinessName(item).toLowerCase();
            const influencer = this.getInfluencerName(item).toLowerCase();

            return (
                filename.includes(q) ||
                business.includes(q) ||
                influencer.includes(q)
            );
            });
        }

        return items;
    },
  },

  created() {
    this.loadAssets();
  },

  methods: {
    ...mapActions("core", [
        "getModeratorUgcAssets",
        "moderatorViewUgcAsset",
        "deleteUgcAsset",
    ]),

    async loadAssets() {
      this.loading = true;
      this.error = "";

      try {
        this.assets = await this.getModeratorUgcAssets();
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Kunne ikke indlæse UGC aktiver.";
      } finally {
        this.loading = false;
      }
    },

    getBusinessName(asset) {
      if (asset.business && asset.business.display_name) {
        return asset.business.display_name;
      }

      if (asset.business && asset.business.company) {
        return asset.business.company;
      }

      return "Business #" + asset.business_id;
    },

    getInfluencerName(asset) {
      if (
        asset.influencer &&
        asset.influencer.user &&
        (asset.influencer.user.first_name || asset.influencer.user.last_name)
      ) {
        return `${asset.influencer.user.first_name || ""} ${asset.influencer.user.last_name || ""}`.trim();
      }

      return "Influencer #" + asset.influencer_id;
    },

    formatStatusLabel(asset) {
        const label = asset.status_label;

        if (!label) {
            return asset.status;
        }

        if (label === "pending_upload") return "Afventer upload";
        if (label === "ready") return "Klar";
        if (label === "failed") return "Fejlede";
        if (label === "deleted") return "Slettet";

        return label;
    },

    getStatusColor(status) {
        const numericStatus = Number(status);

        if (numericStatus === this.ugcAssetStatus.READY) return "success";
        if (numericStatus === this.ugcAssetStatus.PENDING_UPLOAD) return "warning";
        if (numericStatus === this.ugcAssetStatus.FAILED) return "error";
        if (numericStatus === this.ugcAssetStatus.DELETED) return "grey";
        return "grey";
    },

    formatBytes(value) {
      if (!value) return "-";

      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(value) / Math.log(1024));
      return `${(value / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    },

    formatPrice(value) {
      if (value === null || value === undefined) return "-";
      return `DKK ${(value / 100).toFixed(2)}`;
    },

    formatUsageRights(value) {
      if (!value || !value.length) return "-";
      const map = {
        social_media:    "Sociale medier",
        paid_ads:        "Paid ads",
        website:         "Website",
        email_marketing: "Email marketing",
        landing_pages:   "Landingpages",
        print_outdoor:   "Print / outdoor",
      };
      return value.map(v => map[v] || v).join(", ");
    },

    formatRightsPeriod(value) {
      const map = {
        "30":        "30 dage",
        "90":        "90 dage",
        "180":       "6 måneder",
        "365":       "12 måneder",
        "unlimited": "Ubegrænset",
      };
      return map[value] || value || "-";
    },

    async openPreview(asset) {
        this.previewLoading = true;
        this.error = "";
        this.previewUrl = "";
        this.previewAsset = asset;
        this.showPreviewDialog = true;

        try {
            const response = await this.moderatorViewUgcAsset({
                ugc_asset_id: asset.id,
            });

            this.previewUrl = response.url;
        } catch (error) {
            this.error =
            error?.response?.data?.message ||
            error?.message ||
            "Kunne ikke indlæse preview.";
            this.showPreviewDialog = false;
        } finally {
            this.previewLoading = false;
        }
    },

    closePreview() {
        this.showPreviewDialog = false;
        this.previewUrl = "";
        this.previewAsset = null;
    },

    async removeAsset(asset) {
      const confirmed = window.confirm(
        `Delete "${asset.original_filename}"? This will hide it from the platform, but keep the database record.`
      );

      if (!confirmed) {
        return;
      }

      this.deletingAssetId = asset.id;
      this.error = "";

      try {
        await this.deleteUgcAsset({
          ugc_asset_id: asset.id,
        });

        this.assets = this.assets.filter(
          item => String(item.id) !== String(asset.id)
        );
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Could not delete asset.";
      } finally {
        this.deletingAssetId = null;
      }
    },
  },
};
</script>